import { CntrTypeGroupEntity } from "domain/entity/CntrTypeGroup/CntrTypeGroupEntity";
import { ResponseEntity } from "domain/entity/Common/ResponseEntity";
import { MasterDataType } from "domain/entity/MasterData/MasterDataEntity";
import { EMPTY_TARIFF_MAPPING_ENTITY, TariffMappingEntity } from "domain/entity/TariffMapping/TariffMappingEntity";
import { ConsortiumRepository } from "domain/repository/ChargeDetailMaintenance/ConsortiumRepo";
import { ChargeTypeRepository } from "domain/repository/ChargeType/ChargeTypeRepo";
import { CntrTypeGroupRepository } from "domain/repository/CntrTypeGroup/CntrTypeGroupRepo";
import { CompanyRepository } from "domain/repository/Company/CompanyRepo";
import { EquipmentCodeIndMappingRepository } from "domain/repository/EquipmentCodeIndMapping/EquipmentCodeIndMappingRepo";
import { MasterDataRepository } from "domain/repository/MasterData/MasterDataRepo";
import { OpsLineRepository } from "domain/repository/OpsLine/OpsLineRepo";
import { ParameterDetailRepository } from "domain/repository/parameter/ParameterDetailRepo";
import { RequestServiceCodeRepository } from "domain/repository/RequestServiceCode/RequestServiceCodeRepo";
import { ShiftRepository } from "domain/repository/Shift/ShiftRepo";
import { SpecialHandlingIndMappingRepository } from "domain/repository/SpecialHanldingInd/SpecialHandlingIndMappingRepo";
import { SpecialIndRepository } from "domain/repository/SpecialInd/SpecialIndRepo";
import { StandardTariffCodeRepository } from "domain/repository/TariffCode/StandardTariffCodeRepo";
import { TariffTypeRepository } from "domain/repository/TariffCode/TariffTypeRepo";
import { TariffMappingRepository } from "domain/repository/TariffMapping/TariffMappingRepo";
import _ from "lodash";
import { CfsRequestByDroOpts } from "presentation/constant/DropDownOptions/Charge/CfsRequestByDroOpts";
import { tariffMappingCSVColumnMapping } from "presentation/constant/TariffMapping/TariffMappingConstant";
import { createTariffMappingValidationSchema } from "presentation/constant/TariffMapping/TariffMappingValidationSchema";
import { Validation } from "presentation/constant/Validation";
import { DropdownProps } from "presentation/model/DropdownProps";
import { TariffMappingModel } from "presentation/model/TariffMapping/TariffMappingModel";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface TariffMappingVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<TariffMappingModel>> | ((value: SetStateAction<TariffMappingModel>) => void),
    ],
    tariffMappingRepo: TariffMappingRepository,
    chargeTypeRepo: ChargeTypeRepository,
    specialhandlingIndMappingRepo: SpecialHandlingIndMappingRepository,
    opsLineRepo: OpsLineRepository,
    masterDataRepo: MasterDataRepository,
    companyRepo: CompanyRepository,
    parameterDetailRepo: ParameterDetailRepository,
    consortiumRepo: ConsortiumRepository,
    standardTariffCodeRepo: StandardTariffCodeRepository,
    cntrTypeGroupRepo: CntrTypeGroupRepository,
    specialIndRepo: SpecialIndRepository,
    equipmentCodeIndMappingRepo: EquipmentCodeIndMappingRepository,
    shiftRepo: ShiftRepository,
    tariffTypeRepo: TariffTypeRepository,
    requestServiceCodeRepo: RequestServiceCodeRepository,
}

export const TariffMappingVM = ({ dispatch, tariffMappingRepo, chargeTypeRepo, specialhandlingIndMappingRepo,
    opsLineRepo, masterDataRepo, companyRepo, parameterDetailRepo, consortiumRepo, standardTariffCodeRepo, cntrTypeGroupRepo,
    specialIndRepo, equipmentCodeIndMappingRepo, shiftRepo, tariffTypeRepo, requestServiceCodeRepo
}: TariffMappingVMProps) => {
    const [tariffMappingDispatch] = dispatch;

    const loadDropdownOption = async () => {

        await tariffTypeRepo.getAllActiveTariffTypes().then(
            tariffs => {
                let newTariffs = _.orderBy(tariffs, ["tariffType"]);
                let tariffTypeDropdownOptions = newTariffs?.map((entity) => ({
                    dropdownLabel: entity.tariffType,
                    tagLabel: entity.tariffType,
                    value: entity.tariffType,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        tariffTypeDropdownOptions: tariffTypeDropdownOptions,
                    }
                }))
            }
        )

        await chargeTypeRepo.getAllChargeTypesForCombobox().then(
            chargeTypes => {
                let newChargeTypeList = _.orderBy(chargeTypes, ["chargeType", "subChargeType"]);
                //let newChargeTypes = newChargeTypeList.filter(entity => entity.chargeCategory === 'GEN' || entity.chargeCategory === 'SYS');
                let chargeTypeDropdownOption: DropdownProps[] = [];
                let subChargeTypeDropdownOption: { [key: string]: DropdownProps[] } = {};
                newChargeTypeList?.forEach(chgTypeEty => {
                    const chgTypeExisted = chargeTypeDropdownOption?.find(chgType =>
                        chgType.value === chgTypeEty.chargeType);
                    if (!chgTypeExisted) {
                        chargeTypeDropdownOption.push({
                            dropdownLabel: chgTypeEty.chargeType,
                            tagLabel: chgTypeEty.chargeType,
                            value: chgTypeEty.chargeType
                        })
                    }
                    if (chgTypeEty.subChargeType) {
                        if (!subChargeTypeDropdownOption[chgTypeEty.chargeType]) {
                            subChargeTypeDropdownOption[chgTypeEty.chargeType] = [];
                        }
                        subChargeTypeDropdownOption[chgTypeEty.chargeType].push({
                            dropdownLabel: chgTypeEty.subChargeType,
                            tagLabel: chgTypeEty.subChargeType,
                            value: chgTypeEty.subChargeType
                        });
                    }
                });

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        chargeTypeDropdownOptions: chargeTypeDropdownOption,
                        subChargeTypeDropdownOptions: subChargeTypeDropdownOption
                    }
                }))
            }
        )

        await specialhandlingIndMappingRepo.getAllSpecialHandlingIndMappings().then(
            specialHandlingIndList => {
                const uniqueSpecialHandlingIndMap = new Map();
                specialHandlingIndList?.forEach(item => {
                    if (!uniqueSpecialHandlingIndMap.has(item.specialHandlingInd)) {
                        uniqueSpecialHandlingIndMap.set(item.specialHandlingInd, item);
                    }
                });

                const uniqueSpecialHandlingIndList = Array.from(uniqueSpecialHandlingIndMap.values());

                const specialHandlingindDropdownOption = uniqueSpecialHandlingIndList?.map((specialHandlingInd) => ({
                    dropdownLabel: specialHandlingInd.specialHandlingInd,
                    tagLabel: specialHandlingInd.specialHandlingInd,
                    value: specialHandlingInd.specialHandlingInd,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        specHandingIndDropdownOptions: specialHandlingindDropdownOption,
                    }
                }))
            }
        )
        await opsLineRepo.getOpsLineForCombobox().then(
            opsLines => {
                const opsLinesDropdownOptions = opsLines?.map((opsLineEntity) => ({
                    dropdownLabel: opsLineEntity.opsLine,
                    tagLabel: opsLineEntity.opsLine,
                    value: opsLineEntity.opsLine,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        opsLineDropdownOptions: opsLinesDropdownOptions
                    }
                }))
            }
        )


        await masterDataRepo.getMasterDataByKey(MasterDataType.MARSHAL).then(
            marshalEntities => {
                const marshalCodeDropdownOptions = marshalEntities?.map((marshal) => ({
                    dropdownLabel: marshal.code,
                    tagLabel: marshal.code,
                    value: marshal.code,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        marshalCodeDropdownOptions: marshalCodeDropdownOptions,
                    }
                }))
            }
        )

        await masterDataRepo.getMasterDataByKey(MasterDataType.MODALITY).then(
            modalityEntities => {
                const modalityCodeDropdownOptions = modalityEntities?.map((modality) => ({
                    dropdownLabel: modality.code,
                    tagLabel: modality.code,
                    value: modality.code,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        modalityDropdownOptions: modalityCodeDropdownOptions,
                    }
                }))
            }
        )


        await masterDataRepo.getMasterDataByKey(MasterDataType.SERVICE).then(
            serviceCodes => {
                const serviceCodeDropdownOptions = serviceCodes?.map((serviceCode) => ({
                    dropdownLabel: serviceCode.code,
                    tagLabel: serviceCode.code,
                    value: serviceCode.code,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        serviceCodeDropdownOptions: serviceCodeDropdownOptions
                    }
                }))
            }
        );
        await masterDataRepo.getMasterDataByKey(MasterDataType.CHARGE_IND).then(
            chargeInds => {
                const chargeIndDropdownOptions = chargeInds?.map((chargeInd) => ({
                    dropdownLabel: chargeInd.code,
                    tagLabel: chargeInd.code,
                    value: chargeInd.code,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        chargeIndDropdownOptions: chargeIndDropdownOptions
                    }
                }))
            }
        );


        await companyRepo.getAllCompanyForCombobox().then(
            companies => {
                let newCompanies = companies?.filter(company => company.companyType !== 'MASTER' && company.companyType !== 'ALLIANCE');
                let companyCodeDropdownOption = newCompanies?.map((company) => ({
                    dropdownLabel: company.companyCode,
                    tagLabel: company.companyCode,
                    value: company.companyCode,
                })) ?? []
                companyCodeDropdownOption = _.orderBy(companyCodeDropdownOption, "dropdownLabel");

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        companyCodeDropdownOptions: companyCodeDropdownOption,
                    }
                }))
            }
        )

        await parameterDetailRepo.getAllParameterDtlsByParameterCode("MARSHALLING_TYPE").then(
            marshallingTypeList => {
                const marshallingTypeDropdownOption = marshallingTypeList?.map((marshallingType) => ({
                    dropdownLabel: marshallingType.parameterDtlCode,
                    tagLabel: marshallingType.parameterDtlCode,
                    value: marshallingType.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        marshallingTypeDropdownOptions: marshallingTypeDropdownOption
                    }
                }))
            }
        )

        await parameterDetailRepo.getAllParameterDtlsByParameterCode("CNTR_SIZE").then(
            cntrSizeList => {
                const sizeDropdownOption = cntrSizeList?.map((cntrSize) => ({
                    dropdownLabel: cntrSize.parameterDtlCode,
                    tagLabel: cntrSize.parameterDtlCode,
                    value: cntrSize.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        cntrSizeDropdownOptions: sizeDropdownOption
                    }
                }))
            }
        )

        await parameterDetailRepo.getAllParameterDtlsByParameterCode("CNTR_TYPE").then(
            cntrTypeList => {
                const typeDropdownOption = cntrTypeList?.map((cntrType) => ({
                    dropdownLabel: cntrType.parameterDtlCode,
                    tagLabel: cntrType.parameterDtlCode,
                    value: cntrType.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        cntrTypeDropdownOptions: typeDropdownOption
                    }
                }))
            }
        )
        await parameterDetailRepo.getAllParameterDtlsByParameterCode("SHIPMENT_TYPE").then(
            shipmentTypeList => {
                const shipmentTypeDropdownOption = shipmentTypeList?.map((shipmentType) => ({
                    dropdownLabel: shipmentType.parameterDtlCode,
                    tagLabel: shipmentType.parameterDtlCode,
                    value: shipmentType.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        shipmentTypeDropdownOptions: shipmentTypeDropdownOption
                    }
                }))
            }
        )
        await parameterDetailRepo.getAllParameterDtlsByParameterCode("TML_CODE").then(
            tmlCodeList => {
                const tmlCodeDropdownOption = tmlCodeList?.map((tmlCode) => ({
                    dropdownLabel: tmlCode.parameterDtlCode,
                    tagLabel: tmlCode.parameterDtlCode,
                    value: tmlCode.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        terminalCodeDropdownOptions: tmlCodeDropdownOption
                    }
                }))
            }
        )
        /*await parameterDetailRepo.getAllParameterDtlsByParameterCode("OPERATION_TYPE").then(
            operatingTypeList => {
                const operatingTypeDropdownOption = operatingTypeList?.map((operatingType) => ({
                    dropdownLabel: operatingType.parameterDtlCode,
                    tagLabel: operatingType.parameterDtlCode,
                    value: operatingType.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        operationTypeDropdownOptions: operationTypeDropdownOption
                    }
                }))
            }
        )*/

        await parameterDetailRepo.getAllParameterDtlsByParameterCode("LIFT_ON_OFF_IND").then(
            liftOnOffIndList => {
                const liftOnOffIndDropdownOptions = liftOnOffIndList?.map((liftOnOffInd) => ({
                    dropdownLabel: liftOnOffInd.parameterDtlCode,
                    tagLabel: liftOnOffInd.parameterDtlCode,
                    value: liftOnOffInd.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        liftOnOffIndDropdownOptions: liftOnOffIndDropdownOptions
                    }
                }))
            }
        )

        await consortiumRepo.getAllConsortiums().then(
            consortiums => {
                let consortiumCodeDropdownOptions = consortiums?.map((item) => ({
                    dropdownLabel: item.consortiumCode,
                    tagLabel: item.consortiumCode,
                    value: item.consortiumCode,
                })) ?? []
                consortiumCodeDropdownOptions = _.orderBy(consortiumCodeDropdownOptions, "dropdownLabel");

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        consortiumCodeDropdownOptions: consortiumCodeDropdownOptions,
                    }
                }))
            }
        );
        await cntrTypeGroupRepo.getAllCntrTypeGroup().then(
            cntrTypeGroups => {
                const map = new Map<string, CntrTypeGroupEntity>();
                cntrTypeGroups?.forEach(entity => map.set(entity.cntrTypeGroup, entity));
                const newCntrTypeGroups = Array.from(map.values());
                let cntrTypeGroupDropdownOption = newCntrTypeGroups?.map((item) => ({
                    dropdownLabel: item.cntrTypeGroup,
                    tagLabel: item.cntrTypeGroup,
                    value: item.cntrTypeGroup,
                })) ?? []
                cntrTypeGroupDropdownOption = _.orderBy(cntrTypeGroupDropdownOption, "dropdownLabel");

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        cntrTypeGroupDropdownOptions: cntrTypeGroupDropdownOption,
                    }
                }))
            }
        );

        await standardTariffCodeRepo.getAllStandardTariffCodes().then(
            tariffs => {

                let newTariffs = _.orderBy(tariffs, ["tariffType", "tariffCode"]);
                let tariffTypeDropdownOptions: DropdownProps[] = [];
                let tariffCodeDropdownOptions: { [key: string]: DropdownProps[] } = {};
                let allTariffCodeeDropdownOptions: DropdownProps[] = [];

                newTariffs?.forEach(tariff => {
                    const isTariffTypeExisted = tariffTypeDropdownOptions?.find(t =>
                        t.value === tariff.tariffType);
                    if (!isTariffTypeExisted) {
                        tariffTypeDropdownOptions.push({
                            dropdownLabel: tariff.tariffType,
                            tagLabel: tariff.tariffType,
                            value: tariff.tariffType,
                        })
                    }

                    const isTariffCodeExisted = allTariffCodeeDropdownOptions?.find(t =>
                        t.value === tariff.tariffCode);

                    if (!isTariffCodeExisted) {
                        allTariffCodeeDropdownOptions.push({
                            dropdownLabel: tariff.tariffCode,
                            tagLabel: tariff.tariffCode,
                            value: tariff.tariffCode,
                        })
                    }

                    if (tariff.tariffCode) {
                        if (!tariffCodeDropdownOptions[tariff.tariffType]) {
                            tariffCodeDropdownOptions[tariff.tariffType] = [];
                        }
                        tariffCodeDropdownOptions[tariff.tariffType].push({
                            dropdownLabel: tariff.tariffCode,
                            tagLabel: tariff.tariffCode,
                            value: tariff.tariffCode,
                        })

                    }
                })

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        // tariffTypeDropdownOptions: tariffTypeDropdownOptions,
                        tariffCodeDropdownOptions: tariffCodeDropdownOptions,
                    }
                }))
            }
        )

        await masterDataRepo.getMasterDataByKey(MasterDataType.HANDLING_CODE).then(
            handlingCodes => {
                const handlingCodeDropdownOptions = handlingCodes?.map((handlingCode) => ({
                    dropdownLabel: handlingCode.code,
                    tagLabel: handlingCode.code,
                    value: handlingCode.code,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        physicalHandlingCodeDropdownOptions: handlingCodeDropdownOptions
                    }
                }))
            }
        );

        await masterDataRepo.getMasterDataByKey(MasterDataType.FORWARDER_CODE).then(
            forwarderCodes => {
                const forwarderCodesDropdownOptions = forwarderCodes?.map((forwarderCode) => ({
                    dropdownLabel: forwarderCode.code,
                    tagLabel: forwarderCode.code,
                    value: forwarderCode.code,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        forwarderCodeDropdownOptions: forwarderCodesDropdownOptions,
                    }
                }))
            }
        )
        await masterDataRepo.getMasterDataByKey(MasterDataType.TS_LEG_CHG_IND).then(
            transhipmentLegChargeInds => {
                const transhipmentLegChargeIndDropdownOption = transhipmentLegChargeInds?.map((transhipmentLegChargeInd) => ({
                    dropdownLabel: transhipmentLegChargeInd.code,
                    tagLabel: transhipmentLegChargeInd.code,
                    value: transhipmentLegChargeInd.code,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        transhipmentLegChargeIndDropdownOptions: transhipmentLegChargeIndDropdownOption,
                    }
                }))
            }
        )
        await parameterDetailRepo.getAllParameterDtlsByParameterCode("SERVICE_ORDER_CODE").then(
            serviceOrderCodeList => {
                const serviceOrderCodeDropdownOption = serviceOrderCodeList?.map((serviceOrderCode) => ({
                    dropdownLabel: serviceOrderCode.parameterDtlCode,
                    tagLabel: serviceOrderCode.parameterDtlCode,
                    value: serviceOrderCode.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        serviceOrderCodeDropdownOptions: serviceOrderCodeDropdownOption
                    }
                }))
            }
        )
        await parameterDetailRepo.getAllParameterDtlsByParameterCode("CARGO_TYPE").then(
            cargoTypeList => {
                const cargoTypeDropdownOption = cargoTypeList?.map((cargoType) => ({
                    dropdownLabel: cargoType.parameterDtlCode,
                    tagLabel: cargoType.parameterDtlCode,
                    value: cargoType.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        cargoTypeDropdownOptions: cargoTypeDropdownOption
                    }
                }))
            }
        )
        await parameterDetailRepo.getAllParameterDtlsByParameterCode("CARGO_CODE").then(
            cargoCodeList => {
                const cargoCodeDropdownOption = cargoCodeList?.map((cargoCode) => ({
                    dropdownLabel: cargoCode.parameterDtlCode,
                    tagLabel: cargoCode.parameterDtlCode,
                    value: cargoCode.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        cargoCodeDropdownOptions: cargoCodeDropdownOption
                    }
                }))
            }
        )
        await parameterDetailRepo.getParameterThirdDtlByParaCodeAndParaDtlCode("RAILWAY_SETTING", "SPECIAL_AREAS_TO_CHARGE_PER_CNTR").then(
            specialAreaList => {
                const specialAreaDropdownOption = specialAreaList?.map((specialArea) => ({
                    dropdownLabel: specialArea.parameterDtlCode,
                    tagLabel: specialArea.parameterDtlCode,
                    value: specialArea.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        specialAreaDropdownOptions: specialAreaDropdownOption
                    }
                }))
            }
        )
        await specialIndRepo.getSpecialIndForComboBox().then(
            specialIndList => {
                const specialIndDropdownOption = specialIndList?.map((specialInd) => ({
                    dropdownLabel: specialInd.specialInd,
                    tagLabel: specialInd.specialInd,
                    value: specialInd.specialInd,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        specialIndDropdownOptions: specialIndDropdownOption
                    }
                }))
            }
        )

        await equipmentCodeIndMappingRepo.getEquipmenyCodeComboBox().then(
            equipmentCodeInds => {
                const equipmentCodeIndDropdownOption = _.uniqBy(
                    equipmentCodeInds?.map((equipmentCodeInd) => ({
                        dropdownLabel: equipmentCodeInd.equipmentCodeInd,
                        tagLabel: equipmentCodeInd.equipmentCodeInd,
                        value: equipmentCodeInd.equipmentCodeInd,
                    })), 'dropdownLabel') ?? [];

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        equipmentCodeIndDropdownOptions: equipmentCodeIndDropdownOption
                    }
                }));
            }
        )
        await shiftRepo.getShiftCodeForComboBox().then(
            shiftCodes => {
                const shiftCodeDropdownOption = shiftCodes?.map((shiftCode) => ({
                    dropdownLabel: shiftCode.shiftCode,
                    tagLabel: shiftCode.shiftCode,
                    value: shiftCode.shiftCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        shiftCodeDropdownOptions: shiftCodeDropdownOption
                    }
                }))
            }
        )

        await requestServiceCodeRepo.getEntities().then(
            requestService => {

                let newRequestServiceList = _.orderBy(requestService, ["serviceRequestCode", "subServiceRequestCode"]);
                let newRequestServiceCodes = newRequestServiceList.filter(entity => entity.activeInd === 'Y');
                let serviceRequestCodeDropdownOption: DropdownProps[] = [];
                let subServiceRequestCodeDropdownOption: { [key: string]: DropdownProps[] } = {};

                newRequestServiceCodes?.forEach(reqServiceEty => {
                    const serviceReqCodeExisted = serviceRequestCodeDropdownOption?.find(reCode =>
                        reCode.value === reqServiceEty.serviceRequestCode);
                    if (!serviceReqCodeExisted) {
                        serviceRequestCodeDropdownOption.push({
                            dropdownLabel: reqServiceEty.serviceRequestCode,
                            tagLabel: reqServiceEty.serviceRequestCode,
                            value: reqServiceEty.serviceRequestCode
                        })
                    }
                    if (reqServiceEty.subServiceRequestCode) {
                        if (!subServiceRequestCodeDropdownOption[reqServiceEty.serviceRequestCode]) {
                            subServiceRequestCodeDropdownOption[reqServiceEty.serviceRequestCode] = [];
                        }
                        subServiceRequestCodeDropdownOption[reqServiceEty.serviceRequestCode].push({
                            dropdownLabel: reqServiceEty.subServiceRequestCode,
                            tagLabel: reqServiceEty.subServiceRequestCode,
                            value: reqServiceEty.subServiceRequestCode
                        });
                    }
                });

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        serviceReqCodeDropdownOptions: serviceRequestCodeDropdownOption,
                        subServiceReqCodeDropdownOptions: subServiceRequestCodeDropdownOption
                    }
                }))
            }
        )

        await masterDataRepo.getMasterDataByKey(MasterDataType.SOA).then(
            soas => {
                const soaCodeDropdownOptions = soas?.map((soa) => ({
                    dropdownLabel: soa.code,
                    tagLabel: soa.code,
                    value: soa.code,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        soaDropdownOptions: soaCodeDropdownOptions
                    }
                }))
            }
        );

        await parameterDetailRepo.getAllParameterDtlsByParameterCode("MOVEMENT_TYPE").then(
            moveMentTypeList => {
                const moveMentTypeDropdownOption = moveMentTypeList?.map((moveMentType) => ({
                    dropdownLabel: moveMentType.parameterDtlCode,
                    tagLabel: moveMentType.parameterDtlCode,
                    value: moveMentType.parameterDtlCode,
                })) ?? []

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        moveMentTypeDropdownOptions: moveMentTypeDropdownOption
                    }
                }))
            }
        );

        await parameterDetailRepo.getAllParameterDtlsByParameterCode("MARSHALLING_LOCATION_TYPE").then(
            locationTypeList => {
                const locationTypeDropdownOption = locationTypeList?.flatMap((locationType) => {
                    return (locationType.parameterDtlCode || '').split(';').map(part => ({
                        dropdownLabel: part.trim(),
                        tagLabel: part.trim(),
                        value: part.trim(),
                    }));
                }) ?? [];

                tariffMappingDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        locationTypeDropdownOption: locationTypeDropdownOption
                    }
                }));
            }
        );
    }

    const searchAllTariffMappingList = async () => {
        tariffMappingDispatch(prevState => {
            return {
                ...prevState,
                currentRow: { ...EMPTY_TARIFF_MAPPING_ENTITY },
                currentEditRow: { ...EMPTY_TARIFF_MAPPING_ENTITY },
                isShowEditPanel: false,
                isAdd: false,
                isEdit: false,
                updatedRows: [],
                tariffMappingList: [],
            }
        })
        await tariffMappingRepo.searchAllTariffMappingList().then(data => {
            if (data) {
                tariffMappingDispatch(prevState => {
                    return {
                        ...prevState,
                        tariffMappingList: data,
                    }
                })
            } else {
                tariffMappingDispatch(prevState => {
                    return {
                        ...prevState,
                        tariffMappingList: [],
                    }
                })
            }
        })
    }

    const onRowDrag = (rows: TariffMappingEntity[]) => {
        tariffMappingDispatch(prevState => {
            const prevRows = prevState.tariffMappingList;
            const toChangedRows = rows.map((e, index) => ({ ...e, tempPriority: prevRows[index].priority })).filter(e => e.tempPriority !== e.priority);
            return ({
                ...prevState,
                tariffMappingList: rows,
                updatedRows: toChangedRows,
            })
        })
    }

    const onApply = async (updatedRows: TariffMappingEntity[]) => {

        return await tariffMappingRepo.onApply(updatedRows);
    }

    const updateSelectedRows = async (rows: TariffMappingEntity[]) => {

        tariffMappingDispatch(prevState => {
            return {
                ...prevState,
                // tariffMappingList: [...allRows],
                selectedRows: rows,
                forceRefresh: !prevState.forceRefresh
            }
        })
    }

    const onHeaderFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        let tarCode: any;
        tariffMappingDispatch(prevState => {
            // if (fieldKey === 'loaLimitFrom' || fieldKey === 'loaLimitTo' || fieldKey === 'overWeightFrom' || fieldKey === 'overWeightTo'
            //     || fieldKey === 'vesselWeightFrom' || fieldKey === 'vesselWeightTo'
            // ) {
            //     var posIntReg = /^\d*(\.\d{0,2})?$/;
            //     if (!posIntReg.test(val)) {
            //         val = prevState.currentEditRow[fieldKey];
            //     }
            // }
            let tmpCurrentEditRow = prevState.currentEditRow;
            let tmpAllFormState = prevState.masterState.allFormState;

            if (fieldKey === 'chargeType') {
                tmpCurrentEditRow = {
                    ...tmpCurrentEditRow,
                    [fieldKey]: val,
                    subChargeType: val === prevState.currentEditRow.chargeType ? prevState.currentEditRow.subChargeType : ''
                }

                tmpAllFormState = {
                    ...tmpAllFormState,
                    [fieldKey]: '',
                }

            } else if (fieldKey === 'tariffType') {
                tarCode = prevState.dynamicOptions.tariffCodeDropdownOptions[val] ? prevState.dynamicOptions.tariffCodeDropdownOptions[val][0].value : "";
                tmpCurrentEditRow = {
                    ...tmpCurrentEditRow,
                    [fieldKey]: val,
                    tariffCode: tarCode,
                }
                tmpAllFormState = {
                    ...tmpAllFormState,
                    [fieldKey]: '',
                    tariffCode: '',
                }
            } else {
                tmpCurrentEditRow = {
                    ...tmpCurrentEditRow,
                    [fieldKey]: val,
                }
                tmpAllFormState = {
                    ...tmpAllFormState,
                    [fieldKey]: ''
                }
            }

            return {
                ...prevState,
                currentEditRow: {
                    ...tmpCurrentEditRow,
                },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                        ...tmpAllFormState
                    },
                }
            }
        })
    }

    const onRowDoubleClick = async (entity: TariffMappingEntity) => {
        tariffMappingDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: entity,
                currentRow: entity,
                isShowEditPanel: true,
            }
        })
    }

    const onCloseEidtPanel = async () => {
        tariffMappingDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: { ...EMPTY_TARIFF_MAPPING_ENTITY },
                isShowEditPanel: false,
                isAdd: false,
                isEdit: false,
                masterState: {
                    ...prevState.masterState,
                    allFormState: {},
                }
            }
        })
    }

    const onSave = async (currentRow: TariffMappingEntity) => {
        const valResult = await Validation(createTariffMappingValidationSchema).ValidateFormOnly(currentRow);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            tariffMappingDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });

            const res: ResponseEntity = {
                code: "",
                success: false,
                msg: null,
                data: null
            }

            return res;
        }

        Object.keys(currentRow).forEach(key => {
            if (currentRow[key] === '') {
                currentRow[key] = null;
            }
            if (key === 'cfsBy') {
                currentRow[key] = CfsRequestByDroOpts().getToKeyByValue(currentRow[key] as string);
            }
        });
        return tariffMappingRepo.onSave(currentRow);
    }

    const onSaveClicked = () => {
        tariffMappingDispatch(prevState => {
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: true,
                    allFormState: {},
                }
            }
        })
    }

    const onAddClick = async (operatingUnit?: string) => {
        tariffMappingDispatch(prevState => {
            const tariffType = prevState.dynamicOptions?.tariffTypeDropdownOptions?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!))?.[0]?.value ?? '';

            return {
                ...prevState,
                currentEditRow: {
                    ...EMPTY_TARIFF_MAPPING_ENTITY,
                    chargeType: prevState.dynamicOptions?.chargeTypeDropdownOptions?.[0]?.value ?? '',
                    tariffType: tariffType,
                    tariffCode: prevState.dynamicOptions?.tariffCodeDropdownOptions?.[tariffType]?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!))?.[0]?.value ?? ''
                },
                isAdd: true,
                isShowEditPanel: true,
            }
        })
    }

    const onUpload = async (file: File) => {

        const arrayBuffer = await readFileAsArrayBuffer(file);
        const tariffMappingEntities = await processFileData(arrayBuffer);

        return await tariffMappingRepo.uploadData(tariffMappingEntities);
    }

    const readFileAsArrayBuffer = (file: File) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => resolve(e.target?.result);
            reader.onerror = (e) => reject(e.target?.error);

            reader.readAsArrayBuffer(file);
        });
    }


    const processFileData = async (arrayBuffer: any): Promise<TariffMappingEntity[]> => {
        const byteArray = new Uint8Array(arrayBuffer);
        const decoder = new TextDecoder('ascii');
        const content = decoder.decode(byteArray);
        const lines = content.split('\n').map(line => line.trim());

        const result: TariffMappingEntity[] = [];
        if (lines && lines.length === 1 && lines[0] === "") return [];
        lines?.forEach(line => {
            const obj: TariffMappingEntity = { ...EMPTY_TARIFF_MAPPING_ENTITY };
            const lineObjArr = line.split(',');
            for (let i = 0; i < tariffMappingCSVColumnMapping.length && i < lineObjArr.length; i++) {
                obj[tariffMappingCSVColumnMapping[i]] = _.isEmpty(lineObjArr[i]) ? null : lineObjArr[i];
            }
            result.push(obj);
        });

        return result;
    }
    const onCheckboxChange = (checked: boolean, fieldName: string) => {
        tariffMappingDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: {
                    ...prevState.currentEditRow,
                    [fieldName]: checked ? "Y" : "N",
                },
            }
        })
    }

    const onResetClick = async (isAdd: boolean) => {
        tariffMappingDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: isAdd ? { ...EMPTY_TARIFF_MAPPING_ENTITY } : prevState.currentRow,
            }
        })
    }
    const onEditClick = async () => {
        tariffMappingDispatch(prevState => {
            return {
                ...prevState,
                isEdit: true,
            }
        })
    }
    return {
        onEditClick: onEditClick,
        onResetClick: onResetClick,
        onCheckboxChange: onCheckboxChange,
        onUpload: onUpload,
        onAddClick: onAddClick,
        onSave: onSave,
        onCloseEidtPanel: onCloseEidtPanel,
        onRowDoubleClick: onRowDoubleClick,
        onHeaderFieldChange: onHeaderFieldChange,
        updateSelectedRows: updateSelectedRows,
        onApply: onApply,
        onRowDrag: onRowDrag,
        loadDropdownOption: loadDropdownOption,
        searchAllTariffMappingList: searchAllTariffMappingList,
        onSaveClicked: onSaveClicked,
    }
}